var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { AwsUi } from '../../../core/runtime/src';
import { KeyCode } from '../../../core/utils/src/dom';
import { Button } from './button';
import { DropdownItemsCreator, getHref } from './dropdown-items';
import { fitDropdown, scrollToHighlightedOption } from './utils/dropdown-fit-handler';
var SMALL_BREAKPOINT = 768;
var COMPONENT_NAME = 'awsui-button-dropdown';
var prefixClass = function (className) { return COMPONENT_NAME + "-" + className; };
var triggerSelector = "." + prefixClass('container') + " button";
export var ButtonDropdown = AwsUi.__addComponent('awsui-button-dropdown', {
    releaseStatus: 'stable',
    callbacks: {
        initialized: function () {
            var _this = this;
            this.__usingMouse = false;
            this.__keydownHandler = function (ev) {
                if (!_this.__open) {
                    return;
                }
                switch (ev.keyCode) {
                    case KeyCode.escape:
                    case KeyCode.tab:
                        _this.__toggleDropdown();
                        break;
                    case KeyCode.enter:
                        if (_this.__highlightedItem && !getHref(_this.__highlightedItem)) {
                            ev.preventDefault();
                            _this.__keyboardActivateItem(_this.__highlightedFlyOutItem || _this.__highlightedItem);
                        }
                        break;
                    case KeyCode.space:
                        if (_this.__highlightedItem) {
                            // Prevent scrolling the list of items
                            ev.preventDefault();
                        }
                        break;
                    case KeyCode.down:
                        ev.preventDefault();
                        if (_this.__highlightedFlyOutItem) {
                            _this.__moveFlyOutHighlight(1);
                        }
                        else {
                            _this.__moveHighlight(1);
                        }
                        break;
                    case KeyCode.up:
                        ev.preventDefault();
                        if (_this.__highlightedFlyOutItem) {
                            _this.__moveFlyOutHighlight(-1);
                        }
                        else {
                            _this.__moveHighlight(-1);
                        }
                        break;
                    case KeyCode.left:
                    case KeyCode.right:
                        ev.preventDefault();
                        if (_this.__highlightedFlyOutItem) {
                            _this.__highlightedFlyOutItem = null;
                            _this.__expandedItem = null;
                            _this.__moveHighlight(0);
                        }
                        else if (_this.__highlightedItem.items) {
                            _this.__keyboardActivateItem(_this.__highlightedItem);
                            _this.__moveFlyOutHighlight(0);
                        }
                        break;
                }
            };
            this.__keyupHandler = function (ev) {
                if (!_this.__open) {
                    return;
                }
                switch (ev.keyCode) {
                    // We need to handle activating items with Space separately because there is a bug
                    // in Firefox where changing the focus during a Space keydown event it will trigger
                    // unexpected click events on the new element: https://bugzilla.mozilla.org/show_bug.cgi?id=1220143
                    case KeyCode.space:
                        if (_this.__highlightedItem) {
                            ev.preventDefault();
                            _this.__keyboardActivateItem(_this.__highlightedFlyOutItem || _this.__highlightedItem, ev);
                            if (getHref(_this.__highlightedFlyOutItem || _this.__highlightedItem)) {
                                _this.__open = false;
                                _this.focus();
                            }
                        }
                        break;
                }
            };
            this.__globalMouseUpHandler = function (ev) {
                if (!_this.node.contains(ev.target)) {
                    _this.__open = false;
                }
            };
            this.__mouseEnterHandler = function () {
                _this.__usingMouse = true;
            };
            this.__mouseLeaveHandler = function () {
                _this.__usingMouse = false;
            };
            this.__triggerClickHandler = function (event) {
                event.preventDefault();
                if (!_this.__isDisabled()) {
                    _this.__toggleDropdown();
                }
                if (_this.__open) {
                    // When the dropdown was opened with the keyboard we want to pre-select
                    // the first enabled item. Doing this for a dropdown that was opened via mouse
                    // would create a visual disconnect between the highlighted item and the cursor.
                    if (!_this.__usingMouse) {
                        _this.__moveHighlight(1);
                    }
                }
            };
            this.eventListeners = function (action) {
                document[action]('mouseup', _this.__globalMouseUpHandler);
                _this.node[action]('keydown', _this.__keydownHandler);
                _this.node[action]('keyup', _this.__keyupHandler);
                var triggerContainer = _this.node.querySelector('.' + prefixClass('container'));
                triggerContainer[action]('mouseenter', _this.__mouseEnterHandler);
                triggerContainer[action]('mouseleave', _this.__mouseLeaveHandler);
            };
            this.__createItems = DropdownItemsCreator({
                prefixClass: prefixClass,
                getHighlightedItem: function () { return _this.__highlightedFlyOutItem || _this.__highlightedItem; },
                getExpandedItem: function () { return _this.__expandedItem; },
                getExpandableGroups: function () { return _this.expandableGroups; },
                activateItem: this.__activateItem.bind(this),
                isMobile: function () { return _this.__isMobile; }
            });
            this.__defaultTrigger = function (_a) {
                var isDisabled = _a.isDisabled, clickHandler = _a.clickHandler, isOpen = _a.isOpen;
                var props = {
                    disabled: isDisabled,
                    click: clickHandler,
                    icon: 'caret-down-filled',
                    iconAlign: 'right',
                    loading: _this.loading,
                    text: _this.text,
                    variant: _this.variant,
                    formAction: 'none',
                    __ariaExpanded: isOpen ? 'true' : 'false',
                    __ariaHasPopup: 'true'
                };
                return AwsUi.__createElement(Button, __assign({}, props));
            };
            this.__fitDropdown = function (dropdownEl) {
                var triggerEl = _this.node.querySelector(triggerSelector);
                fitDropdown(dropdownEl, triggerEl);
                scrollToHighlightedOption(dropdownEl);
            };
        },
        attached: function () {
            this.eventListeners('addEventListener');
        },
        detached: function () {
            this.eventListeners('removeEventListener');
        }
    },
    properties: {
        // Array of objects, each having the following properties:
        //
        // * `id` [string, mandatory]: allows to identify the item that the user clicked on.
        // * `text` [string, mandatory]: description shown in the menu for this item.
        // * `disabled` [boolean]: whether the item is disabled. Disabled items are not clickable.
        // * `items` [object[]]: an array of item objects. Items will be rendered as nested menu items but
        // only for the first nesting level, multi-nesting is not supported.
        // * `description` [string]: additional data that will be passed to a data-description attribute.
        items: {
            type: 'array',
            itemTypes: ['ButtonDropdown.Item']
        },
        // Renders the button as disabled and prevents clicks.
        disabled: {
            type: 'boolean'
        },
        // Renders the button as being in a loading state. It takes precendence over
        // the `disabled` if both are set to `true`. It prevents clicks.
        loading: {
            type: 'boolean'
        },
        // Determines the general styling of the underlying button. Only `primary` and `normal` variants are supported.
        variant: {
            type: 'string',
            valid: ['normal', 'primary'],
            defaultValue: 'normal'
        },
        // Controls expandability of the item groups
        expandableGroups: {
            type: 'boolean'
        },
        // If true, the component will display the content area.  If false, the component will hide
        // the content area.
        __open: {
            type: 'boolean'
        },
        // Contains the currently highlighted item
        __highlightedItem: {
            type: 'object',
            objectType: 'ButtonDropdown.Item'
        },
        // Contains the currently highlighted fly-out item
        __highlightedFlyOutItem: {
            type: 'object',
            objectType: 'ButtonDropdown.Item'
        },
        // Contains the currently expanded section
        __expandedItem: {
            type: 'object',
            objectType: 'ButtonDropdown.Item'
        },
        __customTrigger: {
            type: 'function'
        },
        // internally defined property describing the layout type
        __isMobile: {
            type: 'boolean'
        }
    },
    elementQueries: [
        {
            read: function () { return window.innerWidth; },
            apply: function (width) {
                this.__isMobile = parseInt(width, 10) <= SMALL_BREAKPOINT;
            }
        }
    ],
    regions: {
        // Text displayed in the button element.
        text: {
            isDefault: true
        }
    },
    functions: {
        // Focuses the underlying native button.
        focus: function () {
            var triggerElement = this.node.querySelector(triggerSelector);
            if (triggerElement) {
                var focusableElement = triggerElement.component ? triggerElement.component : triggerElement;
                focusableElement.focus();
            }
        },
        __toggleDropdown: function () {
            this.__open = !this.__open;
            if (this.__open) {
                this.__highlightedItem = null;
                this.__highlightedFlyOutItem = null;
                this.__expandedItem = null;
            }
            else {
                this.focus();
            }
        },
        __activateItem: function (item, event) {
            if (item.items) {
                if (this.__expandedItem !== item) {
                    this.__expandedItem = item;
                }
                else {
                    this.__expandedItem = null;
                }
            }
            else {
                this.__fireEvent('itemClick', { id: item.id, event: event });
                this.__open = false;
                if (!getHref(item)) {
                    this.focus();
                }
            }
        },
        __keyboardActivateItem: function (item, event) {
            this.__activateItem(item, event);
            if (item.items && !this.__isMobile) {
                // dont change .reduce to .find, because we don't have a polyfill for ie11
                this.__highlightedFlyOutItem = item.items.reduce(function (acc, subItem) { return acc || (!subItem.disabled && subItem); }, null);
                this.__moveFlyOutHighlight(0);
            }
        },
        __isDisabled: function () {
            return this.loading || this.disabled;
        },
        __getEnabledItems: function () {
            var _this = this;
            return this.items.reduce(function (enabledItems, item) {
                if (item.disabled) {
                    return enabledItems;
                }
                if (item.items) {
                    if (_this.expandableGroups && item.text) {
                        enabledItems.push(item);
                    }
                    if ((item === _this.__expandedItem && _this.__isMobile) || !_this.expandableGroups) {
                        item.items.forEach(function (subItem) { return !subItem.disabled && enabledItems.push(subItem); });
                    }
                }
                if (!item.items) {
                    enabledItems.push(item);
                }
                return enabledItems;
            }, []);
        },
        __moveHighlight: function (dir) {
            if (!this.__isMobile) {
                this.__expandedItem = null;
            }
            var items = this.__getEnabledItems();
            if (items.length === 0) {
                return;
            }
            var currIdx = items.indexOf(this.__highlightedItem);
            var nextIdx = currIdx + dir;
            if (items[nextIdx]) {
                // The newly highlighted item receives programmatical focus to ensure the best
                // support for assistive technology to receive updates about the current option.
                this.__highlightedItem = items[nextIdx];
                var itemsSelector = "." + prefixClass('item') + ":not(." + prefixClass('item-disabled') + ")";
                var groupsSelector = "." + prefixClass('category') + ":not(." + prefixClass('category-disabled') + ") > p";
                var allEnabledSelector = itemsSelector + (this.expandableGroups ? "," + groupsSelector : '');
                var enabledItemNodes = this.node.querySelectorAll(allEnabledSelector);
                var nextItemNode = enabledItemNodes[nextIdx];
                var focusableElement = getHref(this.__highlightedItem) ? nextItemNode.querySelector('a') : nextItemNode;
                focusableElement.focus();
            }
        },
        __moveFlyOutHighlight: function (dir) {
            var items = this.__highlightedItem.items;
            var currIdx = items.indexOf(this.__highlightedFlyOutItem);
            var nextIdx = currIdx + dir;
            while (nextIdx > -1 && nextIdx < items.length && items[nextIdx].disabled) {
                nextIdx += dir;
            }
            if (items[nextIdx]) {
                // The newly highlighted item receives programmatical focus to ensure the best
                // support for assistive technology to receive updates about the current option.
                this.__highlightedFlyOutItem = items[nextIdx];
                var itemsSelector = "." + prefixClass('category-highlighted') + " ." + prefixClass('item');
                var itemNodes = this.node.querySelectorAll(itemsSelector);
                var nextItemNode = itemNodes[nextIdx];
                var focusableElement = getHref(this.__highlightedFlyOutItem) ? nextItemNode.querySelector('a') : nextItemNode;
                focusableElement.focus();
            }
        }
    },
    events: {
        // Fired when the user clicks on one of the items.
        // The event `detail` object contains the `id` of the clicked item.
        itemClick: {
            bubbles: true,
            cancelable: false,
            detailType: 'ButtonDropdown.ItemClick'
        }
    },
    render: function () {
        var _this = this;
        var triggerProps = {
            isDisabled: this.__isDisabled(),
            isOpen: this.__open,
            clickHandler: this.__triggerClickHandler
        };
        var createTrigger = this.__customTrigger || this.__defaultTrigger;
        var trigger = createTrigger(triggerProps);
        // Don't render the dropdown if the button is disabled, or if the menu
        // is empty or collapsed
        var isDropdownHidden = !this.items || this.items.length === 0 || this.__isDisabled() || !this.__open;
        var scrollHandler = function () {
            if (!_this.__isMobile) {
                _this.__expandedItem = null;
            }
        };
        var dropdown = isDropdownHidden ? null : (AwsUi.__createElement("div", { className: COMPONENT_NAME, lifecycleKey: COMPONENT_NAME, componentDidMount: this.__fitDropdown },
            AwsUi.__createElement("ul", { role: "menu", className: prefixClass('items'), onscroll: scrollHandler }, this.__createItems(this.items))));
        return AwsUi.__createElement("div", { className: prefixClass('container') }, [trigger, dropdown]);
    }
});
//# sourceMappingURL=button-dropdown.js.map